import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { setStoredToken } from '@/lib/auth-client';

type CallbackSearch = {
  error?: string;
};

export const Route = createFileRoute('/_auth/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => {
    return {
      error: search.error as string | undefined,
    };
  },
  component: CallbackPage,
});

function CallbackPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/callback' });
  const { refreshSession } = useAuth();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const handleCallback = async () => {
      const error = search.error;

      if (error) {
        console.error('OAuth Error:', error);
        navigate({ to: '/login', search: { error } });
        return;
      }

      // OAuth flow sets cookies on redirect - we need to convert to bearer token
      // Small delay to ensure cookies are fully available
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Better Auth bearer token = session token cookie value
        // Format: __Secure-better-auth.session_token=<bearer_token>
        const sessionToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('__Secure-better-auth.session_token='));

        if (sessionToken) {
          // Extract the token value (after the '=')
          const bearerToken = sessionToken.split('=')[1];
          // Decode URI component and remove signature prefix if present
          const token = bearerToken ? decodeURIComponent(bearerToken) : null;

          if (token) {
            setStoredToken(token);
          }
        }

        // Verify session is valid and get user data
        const response = await fetch('/api/auth/get-session', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            // Session is valid, navigate to overview
            navigate({ to: '/overview' });
            return;
          }
        }
      } catch (err) {
        console.error('Session refresh error:', err);
      }

      // If session refresh failed, redirect to login
      navigate({ to: '/login', search: { error: 'auth_failed' } });
    };

    handleCallback();
  }, [navigate, search, refreshSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Completing sign in...</CardTitle>
          <CardDescription>Please wait while we verify your credentials</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    </div>
  );
}
